// Display labels for the read-only Account Deletions dashboard. Every value
// comes from the backend; these only translate codes into words. Unknown
// codes are shown as-is rather than guessed.

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  cancellation_window: 'Cancellation Window',
  awaiting_approval: 'Awaiting Approval',
  approved: 'Approved',
  on_hold: 'On Hold',
  in_progress: 'In Progress',
  needs_review: 'Needs Review',
  failed: 'Failed',
  executed_verification_pending: 'Executed – Verification Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Badge tone per dashboard status.
export const STATUS_TONES: Record<string, 'neutral' | 'info' | 'warning' | 'danger' | 'success'> = {
  pending: 'neutral',
  cancellation_window: 'info',
  awaiting_approval: 'neutral',
  approved: 'info',
  on_hold: 'warning',
  in_progress: 'info',
  needs_review: 'danger',
  failed: 'danger',
  executed_verification_pending: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

export const LEDGER_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open (not completed or cancelled)' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'blocked', label: 'On hold' },
  { value: 'processing', label: 'In progress' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'failed', label: 'Failed' },
  { value: 'completed', label: 'Executed (verified or not yet verified)' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const APPROVAL_LABELS: Record<string, string> = {
  none: 'Not approved',
  pending: 'Approved (not yet used)',
  expired: 'Approval expired',
  used: 'Approval used',
  revoked: 'Approval revoked',
};

export const EXECUTION_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  stopped: 'Stopped – operator review',
  executed: 'Executed',
};

export const HOLD_LABELS: Record<string, string> = {
  active_appointments: 'Upcoming or in-progress bookings',
  unsettled_payments: 'Unsettled payments',
  refund_pending: 'Refund pending',
  stylist_transfer_pending: 'Stylist transfer pending',
  no_show_compensation_pending: 'No-show compensation pending',
  wallet_balance: 'Wallet balance',
  active_subscription: 'Active subscription',
  salon_owner: 'Owns a salon',
  protected_account: 'Protected (admin) account',
  pending_wallet_transactions: 'Wallet transactions in flight',
  wallet_cutover_pending: 'Wallet migration pending',
  connect_balance: 'Stripe Connect balance',
  payout_in_progress: 'Stripe payout in progress',
  stripe_customer_balance: 'Stripe customer balance',
  open_dispute: 'Open payment dispute',
  dispute_check_incomplete: 'Dispute check incomplete',
  payout_check_incomplete: 'Payout check incomplete',
  subscription_check_incomplete: 'Subscription check incomplete',
  notification_review: 'Another user’s notification names this person',
  stripe_check_failed: 'Stripe check could not run',
  residual_check_unavailable: 'Notification check unavailable',
  residual_check_failed: 'Notification check failed',
};

export const OUTSTANDING_LABELS: Record<string, string> = {
  deletion_steps: 'Deletion steps not finished',
  media_pending: 'Media cleanup pending',
  media_abandoned: 'Media that could not be removed',
  media_unmanaged: 'Media outside the managed cloud',
  stripe_connect_closure: 'Stripe Connect closure pending',
  notification_residual_check: 'Notification check not verified',
  legacy_originals_unresolved: 'Legacy Cloudinary originals unresolved',
};

export const READINESS_LABELS: Record<string, string> = {
  cancellation_window_open: 'Cancellation window still open',
  holds_unverified: 'Hold verification unavailable',
  holds_present: 'Holds must be resolved',
  run_in_progress: 'A run is in progress',
  needs_operator_review: 'Needs operator review',
  no_usable_approval: 'No usable approval',
  production_policy_inactive: 'Production deletion policy is inactive',
  emergency_stop_active: 'Emergency stop is active',
  emergency_stop_unknown: 'Emergency stop state could not be read',
};

export const LEGACY_LABELS: Record<string, string> = {
  not_required: 'Not required',
  unverified: 'Not yet checked',
  awaiting_certification: 'Awaiting second-person certification',
  pending: 'Originals remain (pending retirement)',
  none: 'Certified: none',
  retired: 'Certified: retired',
};

export const label = (map: Record<string, string>, code: string | null | undefined): string =>
  code ? map[code] || code : '—';
